import { useState, useEffect } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Flex,
  Header,
  RadioGroup,
  spaces,
  TextInputField,
  Address,
  InternationalPhoneInputField,
} from '@electricjs/arc';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import {
  useAddressValidation,
  AddressForm,
  AddressValidationModal,
} from '@/components/AddressValidation';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { FormActionsContainer } from './common';
import RadioTile from './RadioTile';
import { HiringStatus, OnboardingStep } from './types';

const Demographics = () => {
  const { t } = useTranslation('common');
  const { control, setValue, clearErrors, trigger } = useFormContext();
  const { isValid } = useFormState();
  const hiringStatus = useWatch({ name: 'hiringStatus', control });
  const navigate = useNavigate();
  const isNewHire = hiringStatus === HiringStatus.New;
  const hasComeByNavigation = history.state.idx > 0;
  const [showModal, setShowModal] = useState(false);
  const [originalAddress, setOriginalAddress] = useState<Address | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const currentAddress = useWatch({ name: 'demographics.address', control });

  const {
    validateAddress,
    validationResult,
    isValidating: isAddressValidating,
  } = useAddressValidation();

  useEffect(() => {
    if (originalAddress && !isAddressValidating && !isValidating) {
      if (validationResult?.isExactMatch) {
        setValue('currentStep', OnboardingStep.EmployeeInformation, {
          shouldValidate: true,
        });
      } else {
        setShowModal(true);
      }
    }
  }, [
    originalAddress,
    isAddressValidating,
    isValidating,
    validationResult,
    setValue,
  ]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleAddressValidation = async (currentAddress: Address) => {
    setIsValidating(true);
    setOriginalAddress(currentAddress);

    validateAddress(
      currentAddress,
      () => {
        // Validation completed (either successful with results or no results found)
        // Both cases should show the modal - let the useEffect handle it
        setIsValidating(false);
        // Don't set showModal here - let the useEffect handle it
      },
      error => {
        // Only handle actual errors (like missing required fields)
        console.error('Address validation failed:', error);
        setIsValidating(false);
        // Don't set showModal here - let the useEffect handle it
      }
    );
  };

  const handleModalConfirm = async (selectedAddress: Address) => {
    // Update form fields with selected address
    setValue('demographics.address', selectedAddress, {
      shouldValidate: true,
    });

    // Close modal and proceed
    setShowModal(false);
    setValue('currentStep', OnboardingStep.EmployeeInformation, {
      shouldValidate: true,
    });
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleNext = () => {
    // Only validate address if it has any value other than country
    const hasAddressFields = Object.entries(currentAddress).some(
      ([key, value]) => key !== 'country' && Boolean(value)
    );

    if (hasAddressFields) {
      handleAddressValidation(currentAddress);
    } else {
      // Skip address validation, clean default country value and proceed directly to next step
      setValue('demographics.address.country', '', {
        shouldValidate: true,
      });
      setValue('currentStep', OnboardingStep.EmployeeInformation, {
        shouldValidate: true,
      });
    }
  };

  const handleChangeHiringStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as HiringStatus;
    if (value === HiringStatus.Existing) {
      clearErrors([
        'demographics.personalEmail',
        'demographics.phone',
        'employeeInformation',
      ]);

      // Since the `country` field is automatically filled with a default value,
      // we need to check if it is the only address field filled in.
      // If so, we should clear it, as the address is not required at this stage.
      const hasOnlyCountry =
        !Object.entries(currentAddress).some(
          ([key, value]) => key !== 'country' && value
        ) && Boolean(currentAddress?.country);

      if (hasOnlyCountry) {
        setValue('demographics.address.country', '', {
          shouldValidate: true,
        });
        clearErrors('demographics.address');
      }
    }
  };

  const hasXboardingsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Xboardings
  );

  return (
    <>
      <Header
        title={t('onboardings.demographics.title')}
        subtitle={
          hasXboardingsEntitlement
            ? t('onboardings.demographics.subtitleOnboarding')
            : t('onboardings.demographics.subtitleAdding')
        }
      />
      <Flex width="100%" vertical>
        {hasXboardingsEntitlement ? (
          <RadioGroup
            aria-label="new-hire"
            pb="4.2rem"
            gap="1.2rem"
            onChange={handleChangeHiringStatus}>
            <RadioTile name="hiringStatus" value={HiringStatus.New}>
              {t('onboardings.demographics.newHireLabel')}
            </RadioTile>
            <RadioTile name="hiringStatus" value={HiringStatus.Existing}>
              {t('onboardings.demographics.existingEmployeeLabel')}
            </RadioTile>
          </RadioGroup>
        ) : null}
        <Flex width="100%" gap={spaces.space_m}>
          <TextInputField
            id="firstName"
            name="demographics.firstName"
            control={control}
            label={t('onboardings.demographics.firstNameLabel')}
            required
          />
          <TextInputField
            id="lastName"
            name="demographics.lastName"
            control={control}
            label={t('onboardings.demographics.lastNameLabel')}
            required
          />
        </Flex>
        <TextInputField
          id="personalEmail"
          name="demographics.personalEmail"
          control={control}
          label={t('onboardings.demographics.personalEmailLabel')}
          required={isNewHire}
        />
      </Flex>
      <Flex width="100%" vertical>
        <AddressForm
          name="demographics.address"
          required={isNewHire}
          control={control}
          trigger={trigger}
          onChange={(field, value) => {
            setValue(field, value, {
              shouldValidate: true,
            });
          }}
          showAddressSearch
        />
        {originalAddress && (
          <AddressValidationModal
            isVisible={showModal}
            onClose={handleModalClose}
            onConfirm={handleModalConfirm}
            originalAddress={originalAddress}
            validationResult={validationResult}
          />
        )}
        <InternationalPhoneInputField
          name="demographics.phone"
          control={control}
          label={t('onboardings.demographics.phoneLabel')}
          required={isNewHire}
          placeholder={t('onboardings.demographics.phonePlaceholder')}
        />
      </Flex>
      <FormActionsContainer hidePreviousButton={!hasComeByNavigation}>
        {hasComeByNavigation && (
          <Button
            id="onboarding-demographic-cancel-button"
            variant="outline"
            onClick={handleCancel}>
            {t('onboardings.demographics.cancelButton')}
          </Button>
        )}
        <Button
          id="onboarding-demographic-next-button"
          onClick={handleNext}
          loading={isValidating}
          disabled={!isValid || isValidating}>
          {t('onboardings.demographics.nextButton')}
        </Button>
      </FormActionsContainer>
    </>
  );
};

export default Demographics;
